import { useState } from "react";
import { Box, Button } from "@mui/material";
import StopIcon from "@mui/icons-material/StopCircle";
import PauseIcon from "@mui/icons-material/PauseCircleOutline";
import PlayIcon from "@mui/icons-material/PlayCircleOutline";
import KillIcon from "@mui/icons-material/Bolt";
import WarningIcon from "@mui/icons-material/Warning";
import { v4 as uuidv4 } from "uuid";
import type { IInteractionRpc } from "@pulse-ui/codegen/generated_ts/managed_algo";
import { getDefaultRpcEntries, type DefaultSlot } from "../../utils/defaultRpcs";
import { isRpcDisabledForStatus } from "../../utils/filterInteractionRpcs";
import { useSubmitRpcMutation } from "../../services/algoManagementApi";
import { useAlerts } from "@/hooks";
import { getErrorMessage } from "@/utils/getErrorMessage";

const SLOT_ORDER: DefaultSlot[] = ["stop", "pause", "resume", "delete"];
const SLOT_LABEL: Record<DefaultSlot, string> = {
  start: "Start",
  stop: "Stop",
  pause: "Pause",
  resume: "Resume",
  delete: "Kill",
  update: "Update",
};
const SLOT_ICON: Record<DefaultSlot, React.ReactNode> = {
  start: <PlayIcon fontSize="small" />,
  stop: <StopIcon fontSize="small" />,
  pause: <PauseIcon fontSize="small" />,
  resume: <PlayIcon fontSize="small" />,
  delete: <KillIcon fontSize="small" />,
  update: null,
};

export interface AlgoRpcActionsProps {
  interaction: IInteractionRpc;
  status: string | undefined;
  algoId: string | undefined;
  botId: string;
  onepassToken?: string;
  onSubmitted?: () => void;
}

function rpcRequires(schema: any, fieldName: string): boolean {
  return Array.isArray(schema?.required) && schema.required.includes(fieldName);
}

export const AlgoRpcActions = ({ interaction, status, algoId, botId, onepassToken, onSubmitted }: AlgoRpcActionsProps) => {
  const [submitRpc] = useSubmitRpcMutation();
  const { showError, showSuccess } = useAlerts();
  const [confirmingSlot, setConfirmingSlot] = useState<DefaultSlot | null>(null);
  const [submittingSlot, setSubmittingSlot] = useState<DefaultSlot | null>(null);

  const entries = getDefaultRpcEntries(interaction).filter((e) => e.slot !== "start" && e.slot !== "update");
  const bySlot = new Map(entries.map((e) => [e.slot, e]));
  const ordered = SLOT_ORDER.map((s) => bySlot.get(s)).filter(Boolean) as ReturnType<typeof getDefaultRpcEntries>;

  const fire = async (slot: DefaultSlot, rpcName: string) => {
    const entry = bySlot.get(slot);
    if (entry && rpcRequires(entry.schema, "onepass_token") && !onepassToken) {
      showError(`${SLOT_LABEL[slot]} requires an Onepass Token — fill it in the config tree above and try again.`);
      return;
    }
    const args: Record<string, unknown> = { bot_id: botId };
    if (entry && rpcRequires(entry.schema, "onepass_token") && onepassToken) {
      args.onepass_token = onepassToken;
    }
    setSubmittingSlot(slot);
    try {
      await submitRpc({
        data: {
          req_id: uuidv4(),
          connection_id: (interaction as any)?.session_id,
          algo_id: algoId,
          rpc_name: rpcName,
          args,
        },
      }).unwrap();
      showSuccess(`${SLOT_LABEL[slot]} sent`);
      onSubmitted?.();
    } catch (e: any) {
      showError(`${SLOT_LABEL[slot]} failed: ${getErrorMessage(e)}`);
    } finally {
      setSubmittingSlot(null);
      setConfirmingSlot(null);
    }
  };

  if (confirmingSlot) {
    const entry = bySlot.get(confirmingSlot);
    if (!entry) {
      setConfirmingSlot(null);
      return null;
    }
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1,
          backgroundColor: "rgba(255, 152, 0, 0.08)",
          borderTop: "1px solid rgba(255, 152, 0, 0.4)",
          borderBottom: "1px solid rgba(255, 152, 0, 0.4)",
          fontSize: 13,
          color: "#ffb74d",
        }}
      >
        <WarningIcon fontSize="small" />
        <span>This will terminate the running strategy. Confirm {SLOT_LABEL[confirmingSlot].toLowerCase()}?</span>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="outlined"
          size="small"
          disabled={submittingSlot === confirmingSlot}
          onClick={() => fire(confirmingSlot, entry.rpcName)}
          sx={{
            color: "#ff1744",
            borderColor: "#ff1744",
            "&:hover": { borderColor: "#ff1744", backgroundColor: "rgba(255,23,68,0.08)" },
          }}
        >
          Yes, {SLOT_LABEL[confirmingSlot]}
        </Button>
        <Button size="small" sx={{ color: "#fff" }} onClick={() => setConfirmingSlot(null)}>
          Cancel
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 2,
        py: 0.75,
        borderTop: "1px solid #182a4a",
        backgroundColor: "#0c1838",
      }}
    >
      <span style={{ fontSize: 11, color: "#5d6c8a", letterSpacing: 0.5, textTransform: "uppercase", marginRight: 4 }}>RPC</span>
      {ordered.map((entry) => {
        const disabled = isRpcDisabledForStatus(entry.slot, status) || submittingSlot !== null;
        const isKill = entry.slot === "delete";
        return (
          <Button
            key={entry.slot}
            size="small"
            variant="outlined"
            disabled={disabled}
            startIcon={SLOT_ICON[entry.slot]}
            onClick={() => {
              if (isKill) setConfirmingSlot(entry.slot);
              else fire(entry.slot, entry.rpcName);
            }}
            sx={{
              color: isKill ? "#ff1744" : "#fff",
              borderColor: isKill ? "rgba(255,23,68,0.5)" : "rgba(255,255,255,0.2)",
              textTransform: "none",
              minWidth: 0,
              "&:hover": {
                borderColor: isKill ? "#ff1744" : "rgba(255,255,255,0.5)",
                backgroundColor: isKill ? "rgba(255,23,68,0.08)" : "rgba(255,255,255,0.04)",
              },
              "&.Mui-disabled": { color: "rgba(255,255,255,0.25)", borderColor: "rgba(255,255,255,0.08)" },
            }}
          >
            {SLOT_LABEL[entry.slot]}
          </Button>
        );
      })}
    </Box>
  );
};
